import { type VariantProps, cva } from "class-variance-authority";
import type { HTMLAttributes } from "react";
import { cn } from "../lib/utils";
import { Icon, type IconSymbol } from "./icon";

export const badgeVariants = cva(
  "inline-flex items-center justify-center gap-2 rounded border p-0 min-w-[24px] h-[24px] text-caption text-gray-700 font-title",
  {
    variants: {
      variant: {
        primary: "border-primary text-pink-700",
        filled:
          "bg-primary border-none text-white [text-shadow:0.75px_0.75px_0_rgba(134,19,57,1)]",
        tag: "py-1 px-2 bg-white border-gray-200",
        critical:
          "bg-red-400 border-none text-white [text-shadow:-1px_1px_0_rgba(213,53,106,1)]",
        high: "bg-orange-400 border-none text-white [text-shadow:-1px_1px_0_rgba(207,97,49,1)]",
        medium:
          "bg-yellow-400 border-none text-white [text-shadow:-1px_1px_0_rgba(227,181,56,1)]",
        low: "bg-blue-400 border-none text-white [text-shadow:-1px_1px_0_rgba(93,101,241,1)]",
        conforme:
          "bg-green-400 border-none text-white [text-shadow:-1px_1px_0_rgba(56,167,94,1)]",
        insignificant:
          "bg-gray-400 border-none text-white [text-shadow:-1px_1px_0_rgba(139,147,164,1)]",
      },
    },
    defaultVariants: {
      variant: "primary",
    },
  },
);

export interface BadgeProps
  extends HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {
  icon?: IconSymbol | React.ReactElement;
  action?: BadgeActionProps;
  loading?: boolean;
  iconClassName?: string;
}

export interface BadgeActionProps {
  icon: IconSymbol;
  onClick: () => void;
  className?: string;
}

export function Badge({
  className,
  variant,
  icon,
  action,
  loading = false,
  iconClassName,
  ...props
}: BadgeProps) {
  let customIcon: IconSymbol | React.ReactElement | undefined;
  let iconClass = iconClassName ?? "text-white";
  if (
    typeof variant === "string" &&
    ["critical", "high", "medium"].includes(variant)
  ) {
    customIcon = "triangle-alert";
  } else if (variant === "low") {
    customIcon = "info";
  } else if (variant === "conforme") {
    customIcon = "check";
  } else if (variant === "insignificant") {
    customIcon = "minus";
  } else {
    customIcon = icon;
    iconClass = iconClassName ?? "text-gray-700";
  }

  return (
    <div className={cn(badgeVariants({ variant }), className)} {...props}>
      {loading && (
        <Icon
          icon="loader-circle"
          className={cn("animate-spin group-disabled:text-gray-500")}
          size={16}
          strokeWidth={2}
          aria-hidden={true}
        />
      )}
      {customIcon && !loading && typeof customIcon === "string" ? (
        <Icon icon={customIcon} className={iconClass} />
      ) : (
        customIcon && (
          <span className={cn("flex items-center", iconClassName)}>
            {customIcon}
          </span>
        )
      )}
      {props.children}
      {action && (
        <Icon
          icon={action.icon}
          className={action.className}
          onClick={action.onClick}
          disabled={loading}
          size={12}
        />
      )}
    </div>
  );
}
